import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const STATS = [
  { value: '50+', label: '支持模型' },
  { value: '99.9%', label: '服务可用性' },
  { value: '10K+', label: '活跃用户' },
  { value: '1B+', label: '累计请求' },
];

const FEATURES = [
  {
    icon: '⚡',
    title: '低延迟响应',
    desc: '全球多节点部署，智能路由选择，确保每次请求都能获得最快响应。',
  },
  {
    icon: '🔒',
    title: '企业级安全',
    desc: '数据加密传输，完善的权限管理，保障您的 API 密钥和数据安全。',
  },
  {
    icon: '📊',
    title: '实时监控',
    desc: '完整的调用日志和数据分析面板，让您随时掌握使用情况和成本。',
  },
  {
    icon: '🔀',
    title: '原生路由',
    desc: 'OpenAI、Anthropic、Gemini 各走各自原生接口，路由边界清晰稳定。',
  },
  {
    icon: '💻',
    title: '开发者友好',
    desc: '提供完善的 SDK 和代码示例，支持多种编程语言快速接入。',
  },
  {
    icon: '💰',
    title: '灵活计费',
    desc: '按实际使用量计费，无隐藏费用，支持多种充值方式。',
  },
];

const MODELS = [
  {
    name: 'GPT-5.5',
    vendor: 'OpenAI',
    badge: '最新',
    badgeType: 'primary',
    desc: '当前已启用的新型号，可通过统一接口直接调用',
    tags: ['实时可用', '统一接口', '生产调用'],
    modelId: 'byt-gpt-5.5',
  },
  {
    name: 'GPT-5.4',
    vendor: 'OpenAI',
    desc: '当前已启用的新型号，可通过统一接口直接调用',
    tags: ['实时可用', '统一接口', '生产调用'],
    modelId: 'byt-gpt-5.4',
  },
  {
    name: 'Claude Opus 4.7',
    vendor: 'Anthropic',
    badge: '主力',
    badgeType: 'accent',
    desc: '当前主力高性能型号，适合生产级复杂任务',
    tags: ['实时可用', '统一接口', '复杂任务'],
    modelId: 'byt-claude-opus-4-7',
  },
  {
    name: 'Claude Opus 4 6',
    vendor: 'Anthropic',
    badge: '主力',
    badgeType: 'accent',
    desc: '当前主力高性能型号，适合生产级复杂任务',
    tags: ['实时可用', '统一接口', '复杂任务'],
    modelId: 'byt-claude-opus-4-6',
  },
  {
    name: 'Gemini 3.1 Pro',
    vendor: 'Google',
    badge: '主力',
    badgeType: 'accent',
    desc: '轻量高速型号，适合低延迟和高并发场景',
    tags: ['实时可用', '统一接口', '低延迟'],
    modelId: 'byt-gemini-3.1-pro',
  },
  {
    name: 'Gemini 3',
    vendor: 'Google',
    desc: '轻量高速型号，适合低延迟和高并发场景',
    tags: ['实时可用', '统一接口', '低延迟'],
    modelId: 'byt-gemini-3',
  },
];

const PLANS = [
  {
    name: '免费版',
    subtitle: '适合个人学习和小型项目',
    price: '¥0',
    period: '/月',
    features: ['每月 10,000 令牌', '基础模型访问', '社区支持', '基础 API 限速'],
    cta: '免费开始',
    variant: 'outline',
  },
  {
    name: '专业版',
    subtitle: '适合开发者和初创企业',
    price: '¥99',
    period: '/月',
    features: [
      '每月 500,000 令牌',
      '全部模型访问',
      '优先技术支持',
      '更高 API 限速',
      '调用日志分析',
      '邀请返利',
    ],
    cta: '立即升级',
    variant: 'primary',
    popular: true,
  },
  {
    name: '企业版',
    subtitle: '适合大型企业和团队',
    price: '定制',
    period: '',
    features: [
      '无限令牌额度',
      '私有化部署选项',
      '专属客户经理',
      'SLA 保障',
      '自定义模型接入',
      '发票支持',
    ],
    cta: '联系销售',
    variant: 'outline',
  },
];

const DOC_LINKS = [
  { title: '文档中心', desc: '快速接入' },
  { title: '接口参考', desc: '全部 API 路由与参数' },
  { title: 'OpenAI API', desc: 'Chat、Responses、Images' },
  { title: 'Anthropic API', desc: 'Messages 原生接入' },
  { title: 'Gemini API', desc: 'generateContent 与多模态' },
  { title: 'SDK 与示例', desc: 'JavaScript、Python、cURL' },
  { title: '错误码', desc: '鉴权、限流、上游异常' },
  { title: '接口 FAQ', desc: '常见接入问题与排查' },
];

const CODE_SAMPLES = {
  JavaScript: `const response = await fetch(
  'https://tokenapi.bytrabbit.com/v1/messages',
  {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer your-api-key',
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: 'byt-claude-sonnet-4-20250514',
      max_tokens: 64,
      messages: [{ role: 'user', content: 'Hello!' }]
    }),
  }
);

const data = await response.json();
console.log(data.content?.[0]?.text);`,
  Python: `import requests

response = requests.post(
    "https://tokenapi.bytrabbit.com/v1/messages",
    headers={
        "Content-Type": "application/json",
        "Authorization": "Bearer your-api-key",
        "anthropic-version": "2023-06-01",
    },
    json={
        "model": "byt-claude-sonnet-4-20250514",
        "max_tokens": 64,
        "messages": [{"role": "user", "content": "Hello!"}],
    },
)

data = response.json()
print(data["content"][0]["text"])`,
  cURL: `curl https://tokenapi.bytrabbit.com/v1/messages \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer your-api-key" \\
  -H "anthropic-version: 2023-06-01" \\
  -d '{
    "model": "byt-claude-sonnet-4-20250514",
    "max_tokens": 64,
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`,
};

const FOOTER_LINKS = ['更新日志', 'API 文档', 'SDK 下载', '状态页面', '关于我们', '联系我们'];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [codeTab, setCodeTab] = useState('JavaScript');
  const [copied, setCopied] = useState(false);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(CODE_SAMPLES[codeTab]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-header-inner">
          <div className="dashboard-logo">
            <div className="dashboard-logo-icon">T</div>
            TokenAI
          </div>
          <nav className="dashboard-nav">
            <button type="button" className="btn btn-outline" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => scrollTo('features')}>
              功能特性
            </button>
            <button type="button" className="btn btn-outline" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => scrollTo('models')}>
              模型列表
            </button>
            <button type="button" className="btn btn-outline" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => scrollTo('pricing')}>
              价格方案
            </button>
            <button type="button" className="btn btn-outline" style={{ border: 'none', background: 'none', padding: 0 }} onClick={() => scrollTo('quickstart')}>
              开始使用
            </button>
          </nav>
          <div className="dashboard-header-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/select-school')}>
              切换学校
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/login')}>
              登录
            </button>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-inner">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            新一代 AI 令牌服务平台
          </div>
          <h1>
            构建 AI 应用的
            <br />
            <span className="gradient">最佳令牌服务商</span>
          </h1>
          <p className="hero-desc">
            一站式 AI 模型调用平台，提供 GPT、Claude、Qwen 等主流模型的统一接入服务。
            低延迟、高可用、按需计费。
          </p>
          <div className="hero-actions">
            <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              立即开始
            </button>
            <button type="button" className="btn btn-outline btn-lg" onClick={() => scrollTo('quickstart')}>
              查看文档
            </button>
          </div>
        </div>
      </section>

      <div className="stats-grid">
        {STATS.map((stat) => (
          <div key={stat.label} className="stat-item">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <section id="features" className="dashboard-section">
        <div className="dashboard-section-inner">
          <div className="section-header">
            <span className="section-badge">核心能力</span>
            <h2 className="section-title">为什么选择 TokenAI</h2>
            <p className="section-desc">
              我们提供完整的 AI 模型调用解决方案，让您专注于产品开发
            </p>
          </div>
          <div className="features-grid">
            {FEATURES.map((feature) => (
              <article key={feature.title} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="models" className="dashboard-section">
        <div className="dashboard-section-inner">
          <div className="section-header">
            <span className="section-badge">模型生态</span>
            <h2 className="section-title">支持的 AI 模型</h2>
            <p className="section-desc">
              实时读取后台已启用模型，优先展示各供应商当前可用的新一代主力型号
            </p>
          </div>
          <div className="models-grid">
            {MODELS.map((model) => (
              <article key={model.modelId} className="model-card">
                <div className="model-card-header">
                  <div>
                    <h3>{model.name}</h3>
                    <div className="model-vendor">{model.vendor}</div>
                  </div>
                  {model.badge && (
                    <span className={`model-badge ${model.badgeType === 'accent' ? 'accent' : ''}`}>
                      {model.badge}
                    </span>
                  )}
                </div>
                <p className="model-desc">{model.desc}</p>
                <div className="model-tags">
                  {model.tags.map((tag) => (
                    <span key={tag} className="model-tag">
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="model-id">
                  模型 ID
                  <code>{model.modelId}</code>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="dashboard-section">
        <div className="dashboard-section-inner">
          <div className="section-header">
            <span className="section-badge">定价</span>
            <h2 className="section-title">简单透明的价格</h2>
            <p className="section-desc">选择适合您的方案，随时可以升级或降级</p>
          </div>
          <div className="pricing-grid">
            {PLANS.map((plan) => (
              <article
                key={plan.name}
                className={`pricing-card ${plan.popular ? 'featured' : ''}`}
              >
                {plan.popular && <span className="pricing-popular">最受欢迎</span>}
                <h3>{plan.name}</h3>
                <p className="pricing-subtitle">{plan.subtitle}</p>
                <div className="pricing-price">
                  <span className="amount">{plan.price}</span>
                  {plan.period && <span className="period">{plan.period}</span>}
                </div>
                <ul className="pricing-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`btn btn-full ${plan.variant === 'primary' ? 'btn-primary' : 'btn-outline'}`}
                  onClick={() => navigate(plan.name === '企业版' ? '/login' : '/register')}
                >
                  {plan.cta}
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="quickstart" className="dashboard-section">
        <div className="dashboard-section-inner">
          <div className="section-header">
            <span className="section-badge">开发者</span>
            <h2 className="section-title">快速开始</h2>
            <p className="section-desc">
              直接按供应商原生路由接入。示例分别覆盖 OpenAI、Anthropic、Gemini。
            </p>
          </div>
          <div className="quickstart-layout">
            <div className="doc-links">
              <p style={{ fontSize: '0.875rem', color: 'var(--muted)', margin: '0 0 0.5rem' }}>
                常用 API 文档
              </p>
              {DOC_LINKS.map((link) => (
                <a key={link.title} href="https://token.bytrabbit.com" className="doc-link" target="_blank" rel="noreferrer">
                  <strong>{link.title}</strong>
                  <span>{link.desc}</span>
                </a>
              ))}
            </div>
            <div className="code-panel">
              <div className="code-tabs">
                {Object.keys(CODE_SAMPLES).map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`code-tab ${codeTab === tab ? 'active' : ''}`}
                    onClick={() => setCodeTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="code-header">
                <button type="button" className="code-copy" onClick={handleCopyCode}>
                  {copied ? '已复制' : '复制代码'}
                </button>
              </div>
              <pre className="code-block">
                <code>{CODE_SAMPLES[codeTab]}</code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="cta-box">
          <h2>准备好开始了吗？</h2>
          <p>登录后即可进入控制台、创建令牌、查看模型并开始联调你的 AI 应用。</p>
          <div className="cta-actions">
            <button type="button" className="btn btn-primary btn-lg" onClick={() => navigate('/register')}>
              免费开始使用
            </button>
            <button type="button" className="btn btn-outline btn-lg">
              联系销售团队
            </button>
          </div>
        </div>
      </section>

      <footer className="dashboard-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="dashboard-logo">
              <div className="dashboard-logo-icon">T</div>
              TokenAI
            </div>
            <p>新一代 AI 令牌服务平台，让 AI 触手可及。</p>
          </div>
          <div className="footer-links">
            {FOOTER_LINKS.map((link) => (
              <a key={link} href="https://token.bytrabbit.com" target="_blank" rel="noreferrer">
                {link}
              </a>
            ))}
          </div>
        </div>
        <p className="footer-copy">© {new Date().getFullYear()} TokenAI. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Dashboard;
